import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/db.ts';
import { Loader2 } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface MarketStandardsContent {
  id: string;
  title: string;
  content: string;
  version: number;
  updated_at: string;
}

export function MarketStandardsManager() {
  const [content, setContent] = useState<MarketStandardsContent | null>(null);
  const [title, setTitle] = useState('');
  const [standardsContent, setStandardsContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchCurrentContent();
  }, []);

  const fetchCurrentContent = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .rpc('get_active_site_content', { p_content_type: 'market_standards' });

      if (error) throw error;

      if (data && data.length > 0) {
        const currentContent = data[0];
        setContent(currentContent);
        setTitle(currentContent.title);
        setStandardsContent(currentContent.content);
      }
    } catch (err) {
      setError('Failed to load current market standards');
      console.error('Error fetching content:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      if (!title.trim()) {
        setError('Title is required');
        return;
      }

      if (!standardsContent.trim()) {
        setError('Content is required');
        return;
      }

      const { error } = await supabase
        .rpc('update_site_content', {
          p_content_type: 'market_standards',
          p_title: title,
          p_content: standardsContent
        });

      if (error) throw error;

      setSuccess('Market standards updated successfully');
      await fetchCurrentContent();
    } catch (err) {
      setError('Failed to update market standards');
      console.error('Error updating content:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="bg-white p-8 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6">Wayja Market Standards</h2>
      
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
          {error}
        </div>
      )}
      
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md text-green-600">
          {success}
        </div>
      )}

      <div className="space-y-6">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
            Title
          </label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter title (e.g., Wayja Prediction Market Standards)"
          />
        </div>

        <div>
          <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
            Content
          </label>
          <ReactQuill
            value={standardsContent}
            onChange={(value) => setStandardsContent(value)}
            placeholder="Enter market rules, regulations, and resolution standards..."
            modules={{
              toolbar: [
                [{ 'header': [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                [{ 'color': [] }, { 'background': [] }],
                [{ 'align': [] }],
                ['link'],
                ['clean']
              ]
            }}
            formats={[
              'header',
              'bold', 'italic', 'underline', 'strike',
              'list', 'bullet',
              'color', 'background',
              'align',
              'link'
            ]}
            theme="snow"
            style={{ minHeight: '400px' }}
          />
        </div>

        {content && (
          <div className="text-sm text-gray-500">
            <p>Current version: {content.version}</p>
            <p>Last updated: {new Date(content.updated_at).toLocaleString()}</p>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

